import React, { useCallback, useEffect, ChangeEvent, MouseEvent } from 'react';
import { Package, Plus, Minus } from 'lucide-react';
import { BUCKET_URL } from '../config/config';
import './ProductCard.css'; // You'll need to create this CSS file
import logger from '../utils/logger';

// Type definitions
export interface Product {
  _id: string;
  name: string;
  description?: string;
  price: number;
  quantity: number; // Added this to match ProductPickerPopup's Product interface

  category?: {
    _id: string;
    name: string;
  };
  extraDetails?: {
    unit?: string;
    [key: string]: any;
  };
  images?: Array<{
    filePath: string;
    fileName: string;
  }>;
}

export interface ProductWithQuantity extends Product {
  quantity: number;
}

interface ProductCardProps {
  product: Product;
  isSelected: boolean;
  selectedProduct?: ProductWithQuantity;
  onToggle: (product: Product, event?: MouseEvent) => void;
  onQuantityChange: (productId: string, change: number) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ 
  product, 
  isSelected, 
  selectedProduct, 
  onToggle, 
  onQuantityChange 
}) => {
  useEffect(() => {
    logger.log('ProductCard rendered:', { 
      productId: product._id, 
      productName: product.name,
      isSelected, 
      selectedQuantity: selectedProduct?.quantity 
    });
  }, [product, isSelected, selectedProduct]);

  const handleCardClick = useCallback((e: MouseEvent) => {
    logger.log('Card clicked:', { 
      productId: product._id,
      productName: product.name 
    });
    
    // Prevent toggling if clicked on controls
    if ((e.target as HTMLElement).closest('.quantity-controls')) {
      logger.log('Click on quantity controls - preventing toggle');
      e.stopPropagation();
      return;
    }
    
    logger.log('Calling onToggle from card click');
    onToggle(product, e);
  }, [product, onToggle]);

  const handleCheckboxChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    logger.log('Checkbox changed:', {
      productId: product._id,
      productName: product.name,
      newCheckedState: e.target.checked
    });
    
    e.stopPropagation();
    logger.log('Calling onToggle from checkbox');
    onToggle(product);
  }, [product, onToggle]);

  const handleQuantityDecrease = useCallback((e: MouseEvent) => {
    logger.log('Quantity decrease button clicked:', {
      productId: product._id,
      productName: product.name,
      currentQuantity: selectedProduct?.quantity,
      willDecreaseTo: selectedProduct ? selectedProduct.quantity - 1 : 0
    });
    
    e.stopPropagation();
    e.preventDefault();
    logger.log('Calling onQuantityChange with -1');
    onQuantityChange(product._id, -1);
  }, [product._id, selectedProduct, onQuantityChange]);

  const handleQuantityIncrease = useCallback((e: MouseEvent) => {
    logger.log('Quantity increase button clicked:', {
      productId: product._id,
      productName: product.name,
      currentQuantity: selectedProduct?.quantity,
      willIncreaseTo: selectedProduct ? selectedProduct.quantity + 1 : 1
    });
    
    e.stopPropagation();
    e.preventDefault();
    logger.log('Calling onQuantityChange with +1');
    onQuantityChange(product._id, 1);
  }, [product._id, selectedProduct, onQuantityChange]);

  const handleQuantityInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    logger.log('Quantity input changed:', {
      productId: product._id,
      productName: product.name,
      inputValue: e.target.value,
      currentStoredQuantity: selectedProduct?.quantity
    });
    
    e.stopPropagation();
    
    // Process the input value carefully
    const inputValue = e.target.value.trim();
    
    // Only allow digits and decimal point
    if (/^[0-9]*\.?[0-9]*$/.test(inputValue)) {
      const numValue = parseFloat(inputValue);
      
      logger.log('Parsed quantity value:', {
        inputValue,
        parsedValue: numValue,
        isValidNumber: !isNaN(numValue) && numValue > 0
      });
      
      // Only update if it's a valid number and greater than zero
      if (!isNaN(numValue) && numValue > 0) {
        logger.log('Calling onQuantityChange with direct value:', numValue);
        onQuantityChange(product._id, numValue);
      }
    } else {
      logger.log('Invalid quantity input - ignoring');
    }
  }, [product._id, selectedProduct, onQuantityChange]);

  return (
    <div 
      className={`product-card ${isSelected ? 'selected' : ''}`}
      onClick={handleCardClick}
    >
      {/* Product Image or Placeholder */}
      <div className="product-image">
        {product.images?.[0]?.filePath ? (
          <img 
            src={`${BUCKET_URL}${product.images[0].filePath}`}
            alt={product.name}
            onError={(e) => {
              logger.log('Image load error for product:', product.name);
              e.currentTarget.onerror = null;
              e.currentTarget.style.display = 'none';
              
              if (e.currentTarget.parentElement) {
                e.currentTarget.parentElement.innerHTML = `
                  <div class="image-placeholder">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                      <line x1="12" y1="22.08" x2="12" y2="12"></line>
                    </svg>
                  </div>`;
              }
            }}
            className="product-image-element"
          />
        ) : (
          <div className="image-placeholder">
            <Package size={24} />
          </div>
        )}
        
        {/* Selection Checkbox */}
        <input 
          type="checkbox"
          className="product-checkbox"
          checked={isSelected}
          onChange={handleCheckboxChange}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      
      {/* Product Details */}
      <div className="product-details">
        <h3 className="product-name">{product.name}</h3>
        {product.category && (
          <span className="product-category">{product.category.name}</span>
        )}
        <div className="product-price">
          <span>₹ {product.price.toLocaleString()}</span>
          {product.extraDetails?.unit && (
            <span className="product-unit">per {product.extraDetails.unit}</span>
          )}
        </div>
        
        {/* Quantity Controls (only for selected products) */}
        {isSelected && selectedProduct && (
          <div className="quantity-controls">
            <button 
              className="quantity-btn quantity-decrease"
              onClick={handleQuantityDecrease}
              disabled={selectedProduct.quantity <= 1}
            >
              <Minus size={14} />
            </button>
            
            <input
              type="text"
              className="quantity-value"
              value={selectedProduct.quantity}
              onChange={handleQuantityInputChange}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              style={{ width: '50px', textAlign: 'center' }}
            />
            
            <button 
              className="quantity-btn quantity-increase"
              onClick={handleQuantityIncrease}
            >
              <Plus size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;